import React from "react";
import { FiLock, FiSend, FiUnlock } from "react-icons/fi";
import { SUBMISSION_STATUS_LABELS } from "../../utils/timesheetUtils";

const statusStyles = {
  "not submitted": "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200",
  approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200",
  rejected: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200",
  withdrawn: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  unlocked: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-200",
};

const labelForStatus = (status) =>
  SUBMISSION_STATUS_LABELS[status] || SUBMISSION_STATUS_LABELS["not submitted"];

const TimesheetSubmissionCard = ({
  submission,
  loading,
  totalHours,
  onSubmit,
  onWithdraw,
  submitting,
  withdrawing,
  history = [],
  periodCanSubmit = true,
}) => {
  const status = String(submission?.status || "not submitted").toLowerCase();
  // approved ล็อกเฉพาะตอนที่ period-status ไม่เปิดให้ส่ง (admin ปลดล็อกแล้วจะได้ canSubmit = true)
  const locked = status === "pending" || (status === "approved" && !periodCanSubmit);
  const canSubmit = !locked && periodCanSubmit;
  const showSubmitAction = !locked;
  const rejectionReason = submission?.rejectionReason || submission?.reason || submission?.rejectedReason;

  return (
    <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${locked ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" : "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"}`}>
            {locked ? <FiLock /> : <FiUnlock />}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-gray-900 dark:text-gray-100">การส่งเวลาประจำสัปดาห์</p>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyles[status] || statusStyles["not submitted"]}`}>
                {loading ? "กำลังโหลดสถานะ..." : labelForStatus(status)}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">ชั่วโมงรวม: {totalHours}</p>
            {rejectionReason && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-300">เหตุผลที่ไม่อนุมัติ: {rejectionReason}</p>
            )}
            {status === "unlocked" && (
              <p className="mt-2 text-sm text-indigo-700 dark:text-indigo-300">
                ปลดล็อกให้แก้ไขแล้ว กรุณาแก้ไขและส่งอนุมัติใหม่{submission?.unlockReason ? ` · เหตุผล: ${submission.unlockReason}` : ""}
              </p>
            )}
            {locked && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">สัปดาห์ Timesheet นี้ถูกล็อกระหว่างการตรวจสอบ</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {status === "pending" && (
            <button type="button" onClick={onWithdraw} disabled={withdrawing || loading} className="rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-500/10">
              {withdrawing ? "กำลังถอนการส่ง..." : "ถอนการส่ง"}
            </button>
          )}
          {showSubmitAction && (
            <button type="button" onClick={onSubmit} disabled={!canSubmit || submitting || loading || Number(totalHours) <= 0} className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60">
              <FiSend />
              {submitting ? "กำลังส่ง..." : status === "not submitted" ? "ส่ง Timesheet" : "ส่งอีกครั้ง"}
            </button>
          )}
        </div>
      </div>
      {history.length > 1 && (
        <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">ประวัติการส่ง</p>
          <div className="mt-2 space-y-2">
            {history.slice(0, 3).map((item, index) => (
              <p key={item._id || item.id || index} className="text-sm text-gray-600 dark:text-gray-300">
                #{history.length - index} {labelForStatus(String(item.status || "").toLowerCase())}
                {item.submittedAt || item.createdAt ? ` · ${new Date(item.submittedAt || item.createdAt).toLocaleString()}` : ""}
                {String(item.status || "").toLowerCase() === "unlocked" && item.unlockReason ? ` · เหตุผลที่ปลดล็อก: ${item.unlockReason}` : ""}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetSubmissionCard;
